import type { Page } from '../../types.js';

/**
 * Day two.
 *
 * The honest shape of this page is awkward and is stated rather than smoothed
 * over: the operator console exists and is good, but it is *ours*, and sign-on for
 * a bank's own named staff is not built. So the page leads with the channels a
 * bank genuinely controls today — the webhook, the stream and its own vault — and
 * describes the console as what it is, including who drives it.
 */
const page: Page = {
  slug: 'operations',
  title: 'Operations',
  lede:
    'What your staff and your systems can see once trades are flowing, how a setting gets changed, and what you can hand to an auditor. Including the part that is not finished.',
  group: 'Delivery',
  blocks: [
    {
      kind: 'callout',
      tone: 'warn',
      title: 'Your own staff do not have a console yet',
      text: 'The operator console described below is real and in use, but it is run by us. Sign-on for your named people is planned and not built, and the administrative interface refuses its current development authentication in a production configuration outright. Plan for a request-and-confirm loop with us rather than a switch your product team flips at nine in the morning.',
    },

    { kind: 'heading', text: 'What you can see today, and through what' },
    {
      kind: 'table',
      columns: ['The question', 'Where the answer comes from today', 'Who asks'],
      rows: [
        ['Did that order settle?', 'A signed webhook to your backend, and your own order record', 'Your systems'],
        ['What does this customer hold?', 'The portfolio and holdings endpoints, or the screens we supply', 'Your app, on the customer\'s behalf'],
        ['What has this customer traded?', 'Order history, newest first, paged by cursor', 'Your app or your backend'],
        ['What is the rate right now?', 'A live stream, or polling when streaming is off', 'Every surface'],
        ['Who changed our commission, and when?', 'The audit trail, and the approval record behind it', 'You, by asking us'],
        ['Are we near a plan limit?', 'Call volume by cost class, on the usage screen', 'Us, reported to you'],
        ['Does our book balance?', 'A query over your own vault that must return no rows', 'Your auditor, directly'],
      ],
    },

    { kind: 'heading', text: 'The two channels your systems receive' },
    {
      kind: 'split',
      left: {
        title: 'Webhooks — durable',
        body: [
          {
            kind: 'list',
            items: [
              'Signed with a secret held per bank, with both old and new active during a rotation.',
              'A deterministic identifier, so a re-delivery of the same event can be recognised and ignored.',
              'Bounded retries, with every attempt visible on the webhook screen.',
              'Today the platform emits a settled order. The other event types are declared and reserved but not yet sent.',
            ],
          },
        ],
      },
      right: {
        title: 'The live stream — best effort',
        body: [
          {
            kind: 'list',
            items: [
              'Price ticks, settled and failed orders, portfolio changes, configuration changes and session revocations.',
              'Resumable, so a reconnecting client picks up where it stopped.',
              'A hint to re-read, never the source of truth — a frame can arrive twice, out of order, or not at all.',
              'Switchable off per bank, in which case clients poll instead.',
            ],
          },
        ],
      },
    },
    {
      kind: 'callout',
      tone: 'stop',
      title: 'Verify a webhook before you parse it',
      text: 'Check the signature against the raw bytes, reject anything outside the timestamp window, then dedupe on the event identifier, and only then read the payload. Treating an unverified body as data is the standard way this integration goes wrong.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'The operator console' },
    {
      kind: 'table',
      columns: ['Screen', 'What it shows'],
      rows: [
        ['Tenants', 'Every bank on the platform, and one bank in detail with its resolved configuration'],
        ['Onboarding', 'A request for a new bank, and the ones approved but still waiting for a vault'],
        ['Approvals', 'Changes waiting for a second person, with confidential figures withheld from the queue'],
        ['Features', 'Metals, currencies, widgets, limits and feature switches. Every change is proposed, reviewed, then applied.'],
        ['Commission', 'All four legs, per metal and per currency, with the arithmetic worked through as the server would do it'],
        ['Audit log', 'Who did what, with the request identifier that ties an entry to the response it produced'],
        ['Sessions', 'Live and recently ended customer sessions for one bank'],
        ['Orders', 'Settled and in-flight orders'],
        ['Webhooks', 'Delivery attempts, failures, and the endpoints registered for one bank'],
        ['Usage', 'Call volume by endpoint cost class, against the plan'],
        ['Diagnostics', 'Connection pools, price feed, signing keys and environment — safe to paste into a ticket'],
      ],
    },
    {
      kind: 'prose',
      text: 'Everything on that list exists and works. What is missing is the identity layer that would let your own compliance officer or product owner sign in to it themselves, which is why every one of those screens is currently driven by us on your behalf.',
    },

    { kind: 'heading', text: 'How a setting actually changes' },
    {
      kind: 'ladder',
      steps: [
        {
          title: 'You ask, and one operator proposes the change',
          text: 'It is validated as it is entered. A commission that would price below cost, a currency enabled without commissions behind it, or a metal disabled while still configured is refused at that point rather than days later on a live quote.',
        },
        {
          title: 'A second operator reviews and approves',
          text: 'Enforced in the database: the approver cannot be the requester, and an approval left too long expires instead of lingering. This is the control that stands in for your staff having their own accounts.',
        },
        {
          title: 'The change is applied and versioned',
          text: 'The configuration version increments and an audit row is written. Applying is a separate step from approving, so a failure leaves an approved but unapplied change rather than a half-applied one.',
        },
        {
          title: 'Live surfaces are told and re-read',
          text: 'Every live app and widget is notified and fetches the new configuration. A colour, a limit or a commission changes without a redeploy, an app release or a page reload.',
        },
      ],
    },
    {
      kind: 'callout',
      tone: 'note',
      title: 'Agree the loop before you need it',
      text: 'Two things to settle during rollout: how quickly you need a commission change applied, and who on your side is allowed to ask for one. Both are cheap to agree in advance and awkward to agree during a price move.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'What finance and audit can be given' },
    {
      kind: 'stats',
      items: [
        { value: '0', label: 'Rows the balance check may return', sub: 'Metal and cash, per order', accent: 'teal' },
        { value: 'Per order', label: 'Commission recorded', sub: 'As a figure in its own right', accent: 'amber' },
        { value: 'Per order', label: 'Counterparty cost recorded', sub: 'What settlement actually cost us', accent: 'amber' },
        { value: 'Per customer', label: 'Metal tied to a holder', sub: 'Not a pooled figure', accent: 'violet' },
      ],
    },
    {
      kind: 'prose',
      text: 'Those three used to be entangled or simply not kept, which is the difference between a book that can be reconciled and one that has to be trusted. None of it changes what a customer sees; it changes what can be proved afterwards.',
    },
    {
      kind: 'list',
      items: [
        'Every settled order writes entries that sum to zero in both metal and cash, or the order does not commit.',
        'Your margin on a trade is measured against what the metal actually cost or realised, never against a midpoint.',
        'Where settlement has been confirmed, the difference between the price quoted and the price settled is kept as its own figure, so a negative one is visible rather than absorbed.',
        'Your vault carries its own audit trail, which can be handed to an auditor without extracting anything from a shared system.',
      ],
    },
    {
      kind: 'callout',
      tone: 'tip',
      title: 'Offer this before your reviewers ask for it',
      text: 'The balance check and a read credential on your own database can be handed straight to an auditor. Being able to say that at the start of a finance review is worth more than answering it at the end of one.',
    },

    { kind: 'divider' },

    { kind: 'heading', text: 'What your team still owns after launch' },
    {
      kind: 'list',
      items: [
        'Customer support, including the two questions that come up most: why a rate moved between the quote and the confirmation, and what happens to metal a customer holds.',
        'Your own monitoring of the screens inside your app, which we cannot see.',
        'The wording shown when the market is shut, and any reopening time it names.',
        'Keeping your registered addresses and app identifiers current. A forgotten staging address is a support ticket; an unregistered one is a blank space in your app.',
        'The commercial settings themselves — deciding them, not applying them.',
      ],
    },

    { kind: 'divider' },
    {
      kind: 'cards',
      columns: 3,
      items: [
        {
          title: 'Pricing and commission',
          text: 'What the commission screen is showing, and which half of it you can see.',
          href: '/business/pricing-and-commission',
          accent: 'amber',
        },
        {
          title: 'Security and trust',
          text: 'The approval rule, the audit trail and the unfinished items, in review language.',
          href: '/business/security-and-trust',
          accent: 'violet',
        },
        {
          title: 'Rollout',
          text: 'Where the operations loop needs to be agreed, and by whom.',
          href: '/business/rollout',
          accent: 'teal',
        },
      ],
    },
  ],
};

export default page;
